import {
    Bundle,
    BundleStamp,
    BundleStampFlavor,
    ensureLastTransferForBundle,
    ensureLastValidBundleById,
    ensureStampsForBundle,
    ensureWastesForBundle,
    KingBasura,
    Transfer,
    TruckOrVenueStub,
    useLastTransferForBundle,
    useLastValidBundleById,
    useStampsForBundle,
    useWastesForBundle
} from "../rpc/webrpc-client.gen.ts";
import {isTstzFinite, spawnTstzSpan} from "../tstzspan.ts";
import {makeTruckPlace, makeVenuePlace} from "../tq/tqxx.tsx";
import {z} from "zod/v4";
import {useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {QueryClient, queryOptions, skipToken, useQueries, useQueryClient} from "@tanstack/react-query";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {cleanse, NADA} from "../util/arutil.tsx";
import {ensureWasteStatus, wasteStatusTqo} from "../waste/waste.tsx";
import {mergeUseQueriesWithInfill} from "../tq/mergeUseQueriesWithInfill.tsx";
import {InspectionStage} from "../inspect/stages.tsx";
import {InfallibleUseQueryResult} from "../util/useInfallibleQuery.tsx";
import {useRefreshableInfallibleQuery} from "../util/tqrefresh.tsx";
import {useMothership} from "../tq/tqx.tsx";

const bundleStatusCodes = [
    "UNKNOWN",
    "AWAITING_PICKUP",
    "ONLOADING",
    "ONLOADED",
    "FIELD_APPROVED",
    "DELIVERED",
    "OFFLOADING",
    "INACTIVE",
    "INSPECTED",
] as const;

export const BundleStatusCode = z.enum(bundleStatusCodes);
export type BundleStatusCode = z.infer<typeof BundleStatusCode>;
export const BundleStatus = z.object({
    code: BundleStatusCode,
    place: z.optional(TruckOrVenueStub),
    wasteInspectionStages: InspectionStage.array(),
});
export type BundleStatus = z.infer<typeof BundleStatus>;
export const unknownBundleStatus: BundleStatus = {
    code: BundleStatusCode.enum.UNKNOWN,
    wasteInspectionStages: NADA,
} as const;
export const bundleStatusCodeLabelKeys = {
    UNKNOWN: "unknown",
    AWAITING_PICKUP: "driver.awaitingOnload",
    ONLOADING: "driver.onloading",
    ONLOADED: "onloaded",
    FIELD_APPROVED: "fieldApproved",
    DELIVERED: "delivered",
    OFFLOADING: "driver.offloading",
    INACTIVE: "inactive",
    INSPECTED: "bundleStatus.inspected",
} as const;

export const bundleStatusCodeColor = {
    UNKNOWN: "danger",
    AWAITING_PICKUP: "warning",
    ONLOADING: "primary",
    ONLOADED: "success",
    FIELD_APPROVED: "success",
    DELIVERED: "success",
    OFFLOADING: "primary",
    INACTIVE: "neutral",
    INSPECTED: "primary",
} as const;

export function useBundleStatusLabel(status?: BundleStatus) {
    const {t} = useTranslation();
    if (!status) {
        return undefined;
    }
    return t(bundleStatusCodeLabelKeys[status.code]);
}

// function mergeWasteInspectionStages(wisen: InspectionStage[]): InspectionStage {
//     let rv: InspectionStage | undefined;
//     for (const wis of wisen) {
//         if (!rv && (wis === "FINAL")) {
//             rv = "FINAL";
//         } else if (wis === "NOTHING" && rv === "FINAL") {
//             rv = "NOTHING";
//             break;
//         } else if (wis === "PRELIM") {
//             rv = "PRELIM";
//             break;
//         }
//     }
//     // at this point, we have either (a) short-circuited out on NOTHING or PRELIM,
//     // (b) we saw at least one FINAL, or (c) the list was empty (that shouldn't
//     // ever happen). however, if it is empty, NOTHING is the answer.
//     if (!rv) {
//         rv = "NOTHING";
//     }
//     return rv;
// }

function assembleBundleStatus(
    {
        bundleTqrv, lastTransferTqrv,
        stampsTqrv, wasteInspectionStagesTqrv,
    }: {
        bundleTqrv: InfallibleUseResult<Bundle>,
        lastTransferTqrv: InfallibleUseResult<Transfer | null> | InfallibleUseQueryResult<Transfer | null, Error>,
        stampsTqrv: InfallibleUseResult<BundleStamp[]>,
        wasteInspectionStagesTqrv: InfallibleUseResult<InspectionStage[]>,
    }): InfallibleUseResult<BundleStatus> {
    if (bundleTqrv.isPending || lastTransferTqrv.isPending || stampsTqrv.isPending || wasteInspectionStagesTqrv.isPending) {
        return pendingUseResult;
    }

    const bundle = bundleTqrv.data;
    const lastTransfer = lastTransferTqrv.data;
    const stamps = stampsTqrv.data;
    const wasteInspectionStages = wasteInspectionStagesTqrv.data;

    const fieldApprovalStamp = stamps.find(maybe =>
        maybe.flavor === BundleStampFlavor.enum.FIELD_COMPLETE_ACCEPT) ?? null;
    const isFieldApproved = !!fieldApprovalStamp;
    const deliveryStamp = stamps.find(maybe =>
        maybe.flavor === BundleStampFlavor.enum.DELIVERED) ?? null;
    const isDelivered = !!deliveryStamp
        || (isTstzFinite(lastTransfer?.offloadEnded)
            && lastTransfer?.offloadVenue?.id === bundle.destination?.id);

    const unlabeledRv: Partial<BundleStatus> = {wasteInspectionStages};
    const bundleValidSpan = spawnTstzSpan(bundle.validSpan);
    if (!bundleValidSpan.valid || !bundleValidSpan.containsNow()) {
        unlabeledRv.code = BundleStatusCode.enum.INACTIVE;
    } else if (isDelivered) {
        unlabeledRv.code = BundleStatusCode.enum.DELIVERED;
        if (bundle.destination) {
            unlabeledRv.place = makeVenuePlace(bundle.destination);
        }
    } else if (!lastTransfer) {
        unlabeledRv.code = BundleStatusCode.enum.AWAITING_PICKUP;
        unlabeledRv.place = bundle.origin;
    } else if (isTstzFinite(lastTransfer.offloadEnded)) {
        unlabeledRv.code = BundleStatusCode.enum.AWAITING_PICKUP;
        if (lastTransfer.offloadVenue) {
            unlabeledRv.place = makeVenuePlace(lastTransfer.offloadVenue);
        }
    } else if (isTstzFinite(lastTransfer.onloadStarted) && !isTstzFinite(lastTransfer.onloadEnded)) {
        unlabeledRv.code = BundleStatusCode.enum.ONLOADING;
        if (lastTransfer.onloadVenue) {
            unlabeledRv.place = makeVenuePlace(lastTransfer.onloadVenue);
        }
    } else if (isTstzFinite(lastTransfer.offloadStarted) && !isTstzFinite(lastTransfer.offloadEnded)) {
        unlabeledRv.code = BundleStatusCode.enum.OFFLOADING;
        if (lastTransfer.offloadVenue) {
            unlabeledRv.place = makeVenuePlace(lastTransfer.offloadVenue);
        }
    } else if (isFieldApproved) {
        unlabeledRv.code = BundleStatusCode.enum.FIELD_APPROVED;
        unlabeledRv.place = makeTruckPlace(lastTransfer.truck);
    } else {
        unlabeledRv.code = BundleStatusCode.enum.ONLOADED;
        unlabeledRv.place = makeTruckPlace(lastTransfer.truck);
    }

    const labeledRv = BundleStatus.parse({
        ...unlabeledRv, label: bundleStatusCodeLabelKeys[unlabeledRv.code]
    });
    return successUseResult(labeledRv);
}

export function ensureBundleStatus(args: {
    tqc: QueryClient,
    mama: KingBasura,
    args: { bundleId: string }
}): Promise<BundleStatus> {
    const {tqc, mama} = args;
    return Promise.all([
        ensureLastValidBundleById(args).then(rpc => rpc.bundle),
        ensureLastTransferForBundle(args).then(rpc => rpc.transfer),
        ensureStampsForBundle(args).then(rpc => rpc.stamps),
        ensureWastesForBundle(args).then(wastesTqrv => Promise.all(
            wastesTqrv.wastes.map(stub => ensureWasteStatus({
                tqc, mama, args: {wasteId: stub.id}
            }).then(status => status.inspectionStage))
        ).then(cleanse)),
    ]).then(([bundle, lastTransfer, stamps, wasteInspectionStages]) => {
        const rv = assembleBundleStatus({
            bundleTqrv: successUseResult(bundle),
            lastTransferTqrv: successUseResult(lastTransfer ?? null),
            stampsTqrv: successUseResult(stamps),
            wasteInspectionStagesTqrv: successUseResult(wasteInspectionStages),
        });
        if (!rv.isSuccess) {
            return {
                bundleStub: bundle,
                code: BundleStatusCode.enum.UNKNOWN,
                labelKey: bundleStatusCodeLabelKeys[BundleStatusCode.enum.UNKNOWN],
                wasteInspectionStages: unknownBundleStatus.wasteInspectionStages,
            };
        }
        return rv.data;
    });
}

const bundleStatusVerb = "BundleStatus";

export function bundleStatusTqk(args?: { bundleId: string }) {
    return args ? [bundleStatusVerb, args] as const : [bundleStatusVerb] as const;
}

export function bundleStatusTqo(args?: { tqc: QueryClient, mama: KingBasura, args: { bundleId: string } }) {
    return queryOptions({
        queryKey: bundleStatusTqk(args?.args),
        queryFn: args ? () => ensureBundleStatus(args) : skipToken,
    });
}

export function useBundleStatus(args?: { bundleId: string }) {
    const tqc = useQueryClient();
    const mama = useMothership();
    return useRefreshableInfallibleQuery(bundleStatusTqo(args ? {tqc, mama, args} : undefined));
}

export function BundleStatusRelay(args: { bundleId: string }) {
    const tqc = useQueryClient();
    const mama = useMothership();
    const bundleTqrv = useLastValidBundleById(args);
    const ogLastTransferTqrv = useLastTransferForBundle(args);
    const lastTransferTqrv = ogLastTransferTqrv.isPending
        ? pendingUseResult : successUseResult(ogLastTransferTqrv.data ?? null);
    const stampsTqrv = useStampsForBundle(args);
    const wastesTqrv = useWastesForBundle(args);
    const wasteStatusenTqrv = useQueries(wastesTqrv.data
        ? {
            queries: wastesTqrv.data.map(stub => wasteStatusTqo({tqc, mama, args: {wasteId: stub.id}})),
            combine: mergeUseQueriesWithInfill,
        } : {queries: NADA});
    const wasteInspectionStagesTqrv = wasteStatusenTqrv.isSuccess
        ? successUseResult(cleanse(wasteStatusenTqrv.data.map(status => status?.inspectionStage)))
        : pendingUseResult;

    const tqrv = useMemo(() => assembleBundleStatus({
        bundleTqrv, lastTransferTqrv, stampsTqrv, wasteInspectionStagesTqrv
    }), [bundleTqrv, lastTransferTqrv, stampsTqrv, wasteInspectionStagesTqrv]);

    useEffect(() => {
        if (tqrv.data) {
            tqc.setQueryData(bundleStatusTqk(args), tqrv.data);
        }
    }, [args, tqc, tqrv.data]);

    return null;
}



